import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Loader2, CreditCard, Lock, ShieldCheck, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK);

const stripeAppearance = {
  theme: "night", labels: "floating",
  variables: { colorPrimary: "#D4AF37", colorBackground: "#0F0F0F", colorText: "#F5F5F5", colorTextSecondary: "#A0A0A0", colorDanger: "#EF4444", borderRadius: "12px", fontFamily: "Manrope, sans-serif" },
  rules: { ".Input": { border: "1px solid #2A2A2A", backgroundColor: "#0F0F0F" }, ".Input:focus": { border: "1px solid #D4AF37", boxShadow: "0 0 0 1px #D4AF37" }, ".Tab": { border: "1px solid #2A2A2A", backgroundColor: "#0F0F0F" }, ".Tab--selected": { border: "1px solid #D4AF37", backgroundColor: "#1A1A1A" }, ".Label": { color: "#A0A0A0" } },
};

function ActivatePaymentForm({ token, userId }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoResult, setPromoResult] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const { data } = await axios.post(`${API}/promo/validate`, { code: promoCode });
      setPromoResult(data);
    } catch { setPromoResult({ valid: false, message: "Error validating code." }); }
    finally { setPromoLoading(false); }
  };

  const activationWaived = promoResult?.valid && promoResult?.type === "activation_waiver";
  const totalDue = activationWaived ? 50.00 : 85.00;

  const handleSubmit = async () => {
    if (!stripe || !elements || !agreed) return;
    setLoading(true);
    setError("");

    const { error: confirmError, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: `${window.location.origin}/dashboard` },
      redirect: "if_required",
    });

    if (confirmError) {
      setError(confirmError.message);
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API}/activate/finalize`, {
        token, setup_intent_id: setupIntent.id,
        promo_code: activationWaived ? promoResult.code : "",
      }, { withCredentials: true });
      await checkAuth();
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Activation failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-5">
        <h3 className="font-outfit font-semibold text-[#F5F5F5] mb-4">Order Summary</h3>
        <div className="space-y-2 mb-4">
          <div className="flex justify-between"><span className="text-[#A0A0A0] font-manrope text-sm">Prestigious Wireless Unlimited</span><span className="text-[#F5F5F5] font-manrope">$50.00/mo</span></div>
          <div className="flex justify-between"><span className="text-[#A0A0A0] font-manrope text-sm">One-Time Activation Fee</span>{activationWaived ? <span className="text-emerald-400 font-manrope"><span className="line-through text-[#A0A0A0] mr-2">$35.00</span>$0.00</span> : <span className="text-[#F5F5F5] font-manrope">$35.00</span>}</div>
          {activationWaived && <div className="flex justify-between"><span className="text-emerald-400 font-manrope text-xs">Promo: {promoResult.code}</span><span className="text-emerald-400 font-manrope text-sm">-$35.00</span></div>}
        </div>
        <div className="border-t border-[#2A2A2A] pt-3 flex justify-between">
          <span className="font-outfit font-semibold text-[#F5F5F5]">Due Today</span>
          <span className="font-outfit font-bold text-[#D4AF37] text-xl" data-testid="activate-total">${totalDue.toFixed(2)}</span>
        </div>
      </div>

      {/* Promo Code */}
      <div className="flex gap-2">
        <input value={promoCode} onChange={e => { setPromoCode(e.target.value); setPromoResult(null); }}
          className="flex-1 bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] rounded-xl px-4 py-2.5 text-[#F5F5F5] font-manrope text-sm outline-none uppercase" placeholder="Promo Code (Optional)" data-testid="activate-promo-input" />
        <button onClick={validatePromo} disabled={promoLoading || !promoCode.trim()} className="px-4 py-2.5 border border-[#2A2A2A] hover:border-[#D4AF37] text-[#A0A0A0] hover:text-[#D4AF37] rounded-xl text-sm font-semibold transition-all disabled:opacity-50" data-testid="activate-apply-promo">
          {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
        </button>
      </div>
      {promoResult && (
        <p className={`text-sm font-manrope -mt-2 ${promoResult.valid ? "text-emerald-400" : "text-red-400"}`} data-testid="activate-promo-result">{promoResult.message}</p>
      )}

      <div className="flex items-center gap-3">
        <CreditCard className="w-5 h-5 text-[#D4AF37]" />
        <h2 className="font-outfit text-lg font-semibold text-[#F5F5F5]">Payment Details</h2>
      </div>
      <p className="text-[#A0A0A0] text-xs font-manrope -mt-3">Bank Account (ACH) recommended for lower fees.</p>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm font-manrope" data-testid="activate-error">{error}</div>}

      <div data-testid="activate-payment-element">
        <PaymentElement options={{
          wallets: { applePay: "auto", googlePay: "auto" },
          paymentMethodOrder: ["apple_pay", "google_pay", "card", "us_bank_account"],
        }} />
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-1 w-4 h-4 accent-[#D4AF37]" data-testid="activate-terms" />
        <span className="text-[#A0A0A0] text-sm font-manrope">I agree to the <Link to="/terms" target="_blank" className="text-[#D4AF37] underline">Terms & Conditions</Link> and understand that autopay is required.</span>
      </label>

      <div className="flex items-center gap-2 justify-center text-[#A0A0A0]/60">
        <Lock className="w-3.5 h-3.5" /><span className="text-xs font-manrope">Secured by Stripe. Your data never touches our servers.</span><ShieldCheck className="w-3.5 h-3.5" />
      </div>

      <button onClick={handleSubmit} disabled={loading || !agreed || !stripe}
        className="w-full bg-[#D4AF37] hover:bg-[#F0C541] text-black py-3.5 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-all text-lg"
        data-testid="activate-submit">
        {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</> : `Activate & Pay $${totalDue.toFixed(2)}`}
      </button>
    </div>
  );
}

export default function ActivatePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clientSecret, setClientSecret] = useState(null);
  const [userId, setUserId] = useState(null);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);

  useEffect(() => {
    if (!token) { setError("No activation token provided."); setLoading(false); return; }
    axios.get(`${API}/activate/${token}`).then(({ data }) => {
      setInfo(data); setLoading(false);
    }).catch(err => {
      setError(err.response?.data?.detail || "Invalid or expired activation link.");
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const startPayment = async () => {
    setSetupLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/activate/setup`, { token, password });
      setClientSecret(data.client_secret);
      setUserId(data.user_id);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start payment setup.");
    } finally { setSetupLoading(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" /></div>;

  if (error && !info) return (
    <div className="min-h-screen bg-[#050505] px-6 py-16 flex items-center justify-center">
      <div className="max-w-md text-center">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
          <p className="text-red-400 font-manrope text-lg font-semibold mb-2">Activation Error</p>
          <p className="text-[#A0A0A0] font-manrope text-sm">{error}</p>
        </div>
        <Link to="/login" className="text-[#D4AF37] font-manrope text-sm mt-4 inline-block">Go to Login</Link>
      </div>
    </div>
  );

  if (clientSecret) return (
    <div className="min-h-screen bg-[#050505] px-6 py-12">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <p className="font-outfit text-xl font-bold text-[#D4AF37]">Prestigious Wireless</p>
          <p className="text-[#A0A0A0] text-sm font-manrope mt-1">Complete Your Activation</p>
        </div>
        <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-2xl p-8" data-testid="activate-payment-form">
          <Elements stripe={stripePromise} options={{ clientSecret, appearance: stripeAppearance }}>
            <ActivatePaymentForm token={token} userId={userId} />
          </Elements>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] px-6 py-12">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <p className="font-outfit text-xl font-bold text-[#D4AF37]">Prestigious Wireless</p>
          <p className="text-[#A0A0A0] text-sm font-manrope mt-1">Activate Your Service</p>
        </div>
        <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-2xl p-8" data-testid="activate-info">
          <h2 className="font-outfit text-2xl font-bold text-[#F5F5F5] mb-2">Welcome, {info?.user?.name}!</h2>
          <p className="text-[#A0A0A0] font-manrope text-sm mb-6">Your account is ready. Set your password and add a payment method to activate unlimited service.</p>

          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm mb-4">{error}</div>}

          <div className="space-y-4 mb-6">
            <div className="bg-[#1A1A1A] rounded-xl p-3 flex justify-between"><span className="text-[#A0A0A0] text-sm">Email</span><span className="text-[#F5F5F5] text-sm">{info?.user?.email}</span></div>
            <div>
              <label className="block text-[#A0A0A0] text-sm font-manrope mb-1.5">Set Your Password</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-xl px-4 py-3 text-[#F5F5F5] font-manrope outline-none transition-all pr-12" placeholder="Min 8 characters" data-testid="activate-password" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A0A0A0]">{showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
              </div>
            </div>
          </div>

          <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-4 mb-6">
            <p className="text-[#A0A0A0] text-xs font-manrope uppercase tracking-wider mb-2">What you'll pay</p>
            <div className="flex justify-between mb-1"><span className="text-[#A0A0A0] text-sm">Activation Fee (one-time)</span><span className="text-[#F5F5F5]">$35.00</span></div>
            <div className="flex justify-between mb-2"><span className="text-[#A0A0A0] text-sm">Unlimited Plan (monthly)</span><span className="text-[#F5F5F5]">$50.00</span></div>
            <div className="border-t border-[#2A2A2A] pt-2 flex justify-between"><span className="font-semibold text-[#F5F5F5]">Total Today</span><span className="font-bold text-[#D4AF37] text-lg">$85.00</span></div>
          </div>

          <button onClick={startPayment} disabled={setupLoading || password.length < 8}
            className="w-full bg-[#D4AF37] hover:bg-[#F0C541] text-black py-3.5 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-all text-lg"
            data-testid="activate-continue">
            {setupLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Preparing...</> : <><CreditCard className="w-5 h-5" /> Continue to Payment</>}
          </button>
        </div>
      </div>
    </div>
  );
}
